import { useMemo } from 'react';

import { ProducerColorCode } from '@ravago/shared/page-data/models/elements';
import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Radio } from '@ravago/shared/radiance/components/radio/components/Radio/Radio';
import { RadioGroup } from '@ravago/shared/radiance/components/radio/components/RadioGroup/RadioGroup';
import { useTranslations } from '@ravago/shared/translations/client';

import { hasMtsLocalLeadTime, LeadTimeCheckCriteria } from '../../../../../utils/lead-time';
import { DEFAULT_PRODUCER_COLOR_CODE, OTHER_PRODUCER_COLOR_CODE, UNKNOWN_PRODUCER_COLOR_CODE } from '../../constants';
import { usePathCountryCode } from '@ravago/shared/page-elements-radiance/hooks/use-path-locale';

interface Props {
   colorCodes: ProducerColorCode[];
   leadTimeIndicators: LeadTimeIndicator[];
   colorId: number;
   defaultColorName: string;
   leadTimeEnabled: boolean;
   value: string;
   onChange: (value: string) => void;
   hideExtraColorOptions?: boolean;
}

interface RfqColorCodeRadioProps {
   value: string;
   label: string;
   isMtsLocal: boolean;
}

const RfqColorCodeRadio = ({ value, label, isMtsLocal }: RfqColorCodeRadioProps) => {
   const translate = useTranslations();

   return (
      <Box direction="row" gap="sm" items="center" justify="between" attributes={{ testid: 'color-code-radio' }}>
         <Radio value={value}>{label}</Radio>{' '}
         {isMtsLocal && (
            <Badge leadingIcon="truck" variant="info">
               {translate('request-for-quotation.lead-time.mts-local-days')}
            </Badge>
         )}
      </Box>
   );
};

export const RfqColorCode = ({ colorCodes, colorId, leadTimeIndicators, defaultColorName, value, leadTimeEnabled, onChange, hideExtraColorOptions = false }: Props) => {
   const translate = useTranslations();
   const countryCode = usePathCountryCode();

   const hasColorCodes = colorCodes.length > 0;
   const isDefaultMtsLocal = useMemo(() => {
      if (!leadTimeEnabled) return false;

      const criteria: LeadTimeCheckCriteria = {
         colorId,
         countryCode
      };
      return hasMtsLocalLeadTime(leadTimeIndicators, criteria);
   }, [colorId, countryCode, leadTimeIndicators, leadTimeEnabled]);
   const colorCodeMtsLocalMap = useMemo(() => {
      if (!leadTimeEnabled) return {};

      return colorCodes.reduce((acc, { colorCode }) => {
         const criteria: LeadTimeCheckCriteria = {
            colorId,
            producerColorCode: colorCode,
            countryCode
         };
         acc[colorCode] = hasMtsLocalLeadTime(leadTimeIndicators, criteria);
         return acc;
      }, {} as Record<string, boolean>);
   }, [colorCodes, colorId, countryCode, leadTimeIndicators, leadTimeEnabled]);

   const sortedColorCodes = useMemo(() => {
      const uniqueColorCodes = Array.from(new Map(colorCodes.map((code) => [code.colorCode, code])).values());

      if (!leadTimeEnabled) return uniqueColorCodes;

      const mtsLocalColors = uniqueColorCodes.filter(({ colorCode }) => colorCodeMtsLocalMap[colorCode]);
      const nonMtsLocalColors = uniqueColorCodes.filter(({ colorCode }) => !colorCodeMtsLocalMap[colorCode]);

      return [...mtsLocalColors, ...nonMtsLocalColors];
   }, [leadTimeEnabled, colorCodes, colorCodeMtsLocalMap]);

   return (
      <RadioGroup value={value} onChange={onChange} gap="none">
         {!hasColorCodes && (
            <RfqColorCodeRadio
               value={DEFAULT_PRODUCER_COLOR_CODE}
               label={translate('color-codes.default', { color: defaultColorName })}
               isMtsLocal={isDefaultMtsLocal}
            />
         )}

         {sortedColorCodes.map((opt) => (
            <RfqColorCodeRadio key={opt.colorCode} value={opt.colorCode} label={opt.colorCode} isMtsLocal={colorCodeMtsLocalMap[opt.colorCode]} />
         ))}

         {!hideExtraColorOptions && (
            <RfqColorCodeRadio value={OTHER_PRODUCER_COLOR_CODE} label={translate('color-codes.other')} isMtsLocal={false} />
         )}

         {!hideExtraColorOptions && (
            <RfqColorCodeRadio value={UNKNOWN_PRODUCER_COLOR_CODE} label={translate('color-codes.unknown')} isMtsLocal={false} />
         )}
      </RadioGroup>
   );
};
